import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useOnboarding } from 'features/onboarding/context/OnboardingContext';
import { useOnboardingOptions } from 'features/onboarding/hooks/useOnboardingOptions';
import { extractDocument } from 'features/onboarding/api/extractDocument';
import { resolveBankNameFromCode } from 'features/onboarding/utils/bankCodeMapper';
import type { OcrConflict, OcrConflictChoice } from 'features/onboarding/components/upload/OcrConflictModal';
import type { DependentField, ResetSection } from 'features/onboarding/components/upload/RemoveDocumentModal';
import type {
  BankAccountInfo,
  BusinessInfo,
  DocumentKey,
  FileMeta,
  RepresentativeInfo,
  TaxLegalInfo,
} from 'features/onboarding/types/onboarding.types';

export type ExtractionStatus = 'idle' | 'extracting' | 'success' | 'failed';

interface SectionFills {
  businessInfo?: Partial<BusinessInfo>;
  taxLegalInfo?: Partial<TaxLegalInfo>;
  bankAccountInfo?: Partial<BankAccountInfo>;
  representativeInfo?: Partial<RepresentativeInfo>;
}

interface DependentDescriptor {
  section: ResetSection;
  field: string;
  labelKey: string;
}

/**
 * Form fields each OCR document seeds. Used by the remove-document
 * confirmation (to offer reset) and to label conflicts. Documents without
 * OCR (tax clearance, logo) have no dependents.
 */
const DOC_DEPENDENTS: Record<DocumentKey, DependentDescriptor[]> = {
  osekCertificate: [
    {
      section: 'businessInfo',
      field: 'ownerName',
      labelKey: 'businessInfo.contactPerson',
    },
    { section: 'taxLegalInfo', field: 'taxId', labelKey: 'taxLegal.taxId' },
    {
      section: 'taxLegalInfo',
      field: 'officialBusinessName',
      labelKey: 'taxLegal.officialBusinessName',
    },
    {
      section: 'businessInfo',
      field: 'commercialName',
      labelKey: 'businessInfo.commercialName',
    },
  ],
  bankConfirmation: [
    { section: 'bankAccountInfo', field: 'bankName', labelKey: 'bankAccount.bankName' },
    {
      section: 'bankAccountInfo',
      field: 'bankBranchNumber',
      labelKey: 'bankAccount.bankBranchNumber',
    },
    {
      section: 'bankAccountInfo',
      field: 'bankAccountNumber',
      labelKey: 'bankAccount.bankAccountNumber',
    },
    {
      section: 'bankAccountInfo',
      field: 'bankAccountOwnerName',
      labelKey: 'bankAccount.bankAccountOwnerName',
    },
    {
      section: 'bankAccountInfo',
      field: 'bankAccountOwnerId',
      labelKey: 'bankAccount.bankAccountOwnerId',
    },
  ],
  taxClearance: [],
  businessLogo: [],
};

interface UseStepDocumentUploadOptions {
  docKey: DocumentKey;
  /** When true, a successful upload is sent to /ocr/extract to prefill fields. */
  ocr: boolean;
}

export interface UseStepDocumentUploadReturn {
  file?: FileMeta;
  status: ExtractionStatus;
  busy: boolean;
  /** Pass to FileUpload `onChange` (fires for both add and remove). */
  onChange: (meta: FileMeta | null) => void;
  /** Pass to FileUpload `onUploaded` (fires only after a successful upload). */
  onUploaded: (meta: FileMeta) => void;
  /** Pass to FileUpload `onRequestRemove` for OCR docs (opens the reset modal). */
  onRequestRemove?: () => void;
  conflictModal: {
    open: boolean;
    conflicts: OcrConflict[];
    documentLabel: string;
    onClose: () => void;
    onResolve: (choices: OcrConflictChoice[]) => void;
  };
  removeModal: {
    open: boolean;
    docKey: DocumentKey;
    documentLabel: string;
    dependents: DependentField[];
    onClose: () => void;
    onConfirm: (resetFields: DependentField[]) => void;
  };
}

/**
 * Encapsulates the per-document upload + OCR-prefill behaviour that used to
 * live in the monolithic documents step, so each form step can drop a single
 * document slot inline. Handles the extraction call, empty-field auto-fill,
 * the replace-conflict modal, and the dependent-field reset modal.
 */
export function useStepDocumentUpload({
  docKey,
  ocr,
}: UseStepDocumentUploadOptions): UseStepDocumentUploadReturn {
  const { t } = useTranslation();
  const { data, setDocument, updateSection } = useOnboarding();
  const { data: onboardingOptions } = useOnboardingOptions();

  const [status, setStatus] = useState<ExtractionStatus>('idle');
  const [conflicts, setConflicts] = useState<OcrConflict[]>([]);
  const [pendingFills, setPendingFills] = useState<SectionFills>({});
  const [removeOpen, setRemoveOpen] = useState(false);

  const previousFileRef = useRef<FileMeta | undefined>(undefined);

  const documentLabel = t(`documents.${docKey}`) as string;

  const valueOf = (section: ResetSection, field: string): string => {
    const sect = data[section] as unknown as Record<string, unknown>;
    const v = sect[field];
    return typeof v === 'string' ? v : '';
  };

  const applyFills = (fills: SectionFills) => {
    if (fills.businessInfo && Object.keys(fills.businessInfo).length) {
      updateSection('businessInfo', fills.businessInfo);
    }
    if (fills.taxLegalInfo && Object.keys(fills.taxLegalInfo).length) {
      updateSection('taxLegalInfo', fills.taxLegalInfo);
    }
    if (fills.bankAccountInfo && Object.keys(fills.bankAccountInfo).length) {
      updateSection('bankAccountInfo', fills.bankAccountInfo);
    }
    if (fills.representativeInfo && Object.keys(fills.representativeInfo).length) {
      updateSection('representativeInfo', fills.representativeInfo);
    }
  };

  const onChange = (meta: FileMeta | null) => {
    previousFileRef.current = data.documents[docKey];
    setDocument(docKey, meta);
    if (!meta) {
      setStatus('idle');
    }
  };

  const onUploaded = async (meta: FileMeta) => {
    if (!ocr) return;

    const previousFile = previousFileRef.current;
    const isReplacement = Boolean(previousFile && previousFile.url !== meta.url);

    setStatus('extracting');
    const result = await extractDocument({ docType: docKey, url: meta.url });

    if (!result.ok) {
      setStatus('failed');
      return;
    }

    // Resolve the bank dropdown value from the numeric bank code the OCR
    // returned (Pipedrive labels embed the Bank of Israel code in trailing
    // parentheses, so we match by that suffix).
    if (result.bank && result.fields.bankCode) {
      const resolvedBankName = resolveBankNameFromCode(
        result.fields.bankCode,
        onboardingOptions?.banks,
      );
      if (resolvedBankName) {
        result.bank.bankName = resolvedBankName;
      }
    }

    const fills: SectionFills = {};
    const detected: OcrConflict[] = [];

    const consider = (
      section: ResetSection,
      field: string,
      labelKey: string,
      newValue: string | undefined | null,
    ) => {
      if (!newValue) return;
      const current = valueOf(section, field);
      if (!current) {
        const sect = (fills[section] ??= {}) as Record<string, string>;
        sect[field] = newValue;
        return;
      }
      if (current === newValue) return;
      detected.push({
        section,
        field,
        label: t(labelKey) as string,
        oldValue: current,
        newValue,
      });
    };

    if (result.business) {
      consider(
        'businessInfo',
        'ownerName',
        'businessInfo.contactPerson',
        result.business.ownerName,
      );
      consider(
        'businessInfo',
        'commercialName',
        'businessInfo.commercialName',
        result.business.commercialName,
      );
    }
    if (result.tax) {
      consider('taxLegalInfo', 'taxId', 'taxLegal.taxId', result.tax.taxId);
      consider(
        'taxLegalInfo',
        'officialBusinessName',
        'taxLegal.officialBusinessName',
        result.tax.officialBusinessName,
      );
    }
    if (result.bank) {
      consider(
        'bankAccountInfo',
        'bankAccountOwnerId',
        'bankAccount.bankAccountOwnerId',
        result.bank.bankAccountOwnerId,
      );
      consider('bankAccountInfo', 'bankName', 'bankAccount.bankName', result.bank.bankName);
      consider(
        'bankAccountInfo',
        'bankBranchNumber',
        'bankAccount.bankBranchNumber',
        result.bank.bankBranchNumber,
      );
      consider(
        'bankAccountInfo',
        'bankAccountNumber',
        'bankAccount.bankAccountNumber',
        result.bank.bankAccountNumber,
      );
      consider(
        'bankAccountInfo',
        'bankAccountOwnerName',
        'bankAccount.bankAccountOwnerName',
        result.bank.bankAccountOwnerName,
      );
    }
    if (result.representative) {
      consider(
        'representativeInfo',
        'representativeName',
        'representative.representativeName',
        result.representative.representativeName,
      );
      consider(
        'representativeInfo',
        'representativeId',
        'representative.representativeId',
        result.representative.representativeId,
      );
    }

    // First-time uploads just auto-fill empty fields — no conflict to resolve.
    if (!isReplacement || detected.length === 0) {
      applyFills(fills);
      setStatus('success');
      return;
    }

    setPendingFills(fills);
    setConflicts(detected);
    setStatus('success');
  };

  const handleConflictResolve = (choices: OcrConflictChoice[]) => {
    const merged: SectionFills = {
      businessInfo: { ...(pendingFills.businessInfo ?? {}) },
      taxLegalInfo: { ...(pendingFills.taxLegalInfo ?? {}) },
      bankAccountInfo: { ...(pendingFills.bankAccountInfo ?? {}) },
      representativeInfo: { ...(pendingFills.representativeInfo ?? {}) },
    };
    conflicts.forEach((c, i) => {
      if (choices[i] === 'new') {
        const bucket = (merged[c.section] ??= {}) as Record<string, string>;
        bucket[c.field] = c.newValue;
      }
    });
    applyFills(merged);
    setConflicts([]);
    setPendingFills({});
  };

  const handleConflictCancel = () => {
    setConflicts([]);
    setPendingFills({});
  };

  const buildDependents = (): DependentField[] =>
    DOC_DEPENDENTS[docKey].map((d) => ({
      section: d.section,
      field: d.field,
      label: t(d.labelKey) as string,
      currentValue: valueOf(d.section, d.field),
    }));

  const handleRemoveConfirm = (resetFields: DependentField[]) => {
    setDocument(docKey, null);
    setStatus('idle');
    previousFileRef.current = undefined;

    const grouped: Record<string, Record<string, string>> = {};
    resetFields.forEach((f) => {
      (grouped[f.section] ??= {})[f.field] = '';
    });
    (Object.keys(grouped) as ResetSection[]).forEach((section) => {
      updateSection(section, grouped[section] as never);
    });

    setRemoveOpen(false);
  };

  return {
    file: data.documents[docKey],
    status,
    busy: ocr && status === 'extracting',
    onChange,
    onUploaded,
    onRequestRemove: ocr ? () => setRemoveOpen(true) : undefined,
    conflictModal: {
      open: conflicts.length > 0,
      conflicts,
      documentLabel,
      onClose: handleConflictCancel,
      onResolve: handleConflictResolve,
    },
    removeModal: {
      open: removeOpen,
      docKey,
      documentLabel,
      dependents: buildDependents(),
      onClose: () => setRemoveOpen(false),
      onConfirm: handleRemoveConfirm,
    },
  };
}
